import * as PIXI from "pixi.js";
import { FX } from "revolt-fx";

/**
 * How many trail particles may be in the air across ALL trails at once.
 *
 * Replaces the 2000 trail.json ships — see initTrailBundle, which explains why
 * that number made a volley of trails thinner than a single one.
 */
const TRAIL_FX_MAX_PARTICLES = 20000;

/**
 * FXManager — Singleton RevoltFX manager for ALL particle effects.
 *
 * Manages a single FX instance that loads multiple effect bundles
 * (lightning bolts, rain, etc). RevoltFX's loadBundleFiles() uses
 * hardcoded PIXI aliases (rfx_spritesheet, rfx_bundleSettings), so
 * only the first bundle uses it. Additional bundles load with unique
 * PIXI aliases and register emitters/textures directly — no collisions.
 *
 * Usage:
 *   await FXManager.init(bundleUrl, spritesheetUrl);                     // First bundle
 *   await FXManager.loadAdditionalBundle(url, ssUrl, 'rain');            // More bundles
 *   const bolt = FXManager.emitLightning(container, startPos, endPos);   // Lightning
 *   FXManager.startRain(container);                                      // Rain
 *   FXManager.stopRain();                                                // Stop rain
 *   FXManager.destroy();                                                 // Cleanup
 */



class FXManagerClass {
  constructor() {
    this._fx = null;
    this._initialized = false;
    this._initializing = false;
    this._tickerFn = null;
    this._activeRain = null; // { wrapper, emitter }
    this._activeCoinShower = null; // { wrapper, emitter }
    this._bundleCount = 0; // Tracks loaded bundles for unique ID offsets

    // Dedicated FX instance for trail particles (separate from lightning _fx)
    this._trailFx = null;
    this._trailTickerFn = null;
    this._trailReady = false;
    // Cloned emitter settings, keyed by the look written onto them — see
    // emitTrail. Cleared whenever the FX instance behind them goes.
    this._trailSettingsCache = new Map();

    this._poolsWarmed = false; // guard so warmUpPools() runs at most once
    this._fxOwnerSeq = 0; // unique owner id per bolt/ball emit (concurrent-safe teardown)
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Initialize RevoltFX and load the first effect bundle.
   * Safe to call multiple times — only loads once.
   * @param {string} bundleUrl - Path to the RevoltFX bundle JSON
   * @param {string} spritesheetUrl - Path to the spritesheet JSON (atlas + texture)
   * @returns {Promise<boolean>} true if initialized successfully
   */
  async init(bundleUrl, spritesheetUrl) {
    if (this._initialized) return true;
    if (this._initializing) return false;

    this._initializing = true;

    try {
      this._fx = new FX();

      await this._fx.loadBundleFiles(bundleUrl, spritesheetUrl);

      // Register per-frame update on the shared PixiJS ticker
      this._tickerFn = () => {
        if (this._fx) {
          try { this._fx.update(); } catch { /* particle referencing destroyed container */ }
        }
      };
      PIXI.Ticker.shared.add(this._tickerFn);


      this._initialized = true;
      this._initializing = false;

      return true;
    } catch (error) {
      this._initializing = false;
      this._fx = null;
      return false;
    }
  }

  /**
   * Load an additional RevoltFX bundle into the same FX instance.
   *
   * RevoltFX's loadBundleFiles() uses hardcoded PIXI aliases
   * (rfx_spritesheet, rfx_bundleSettings), so calling it twice returns
   * the first bundle's cached data for the second — breaking it.
   *
   * This method bypasses that by:
   * 1. Loading assets with unique PIXI aliases (no cache collision)
   * 2. Registering emitters via addParticleEmitter (offset IDs avoid collision)
   * 3. Registering sprite textures via initSprite
   *
   * @param {string} bundleUrl - Path to the RevoltFX bundle JSON
   * @param {string} spritesheetUrl - Path to the spritesheet atlas JSON
   * @param {string} aliasPrefix - Unique prefix for PIXI aliases (e.g. 'rain')
   * @returns {Promise<boolean>} true if loaded successfully
   */
  /**
   * Add a second bundle's emitters to the TRAIL instance (initTrailBundle
   * must have run). Cleopatra's Fire: Zeus's `particle_trail` (`trail_coin`,
   * soft_glow, additive) beside trail.json, so emitTrail can fire either by
   * name. IDs are offset so the two bundles never collide — the same scheme
   * loadAdditionalBundle uses for the main instance.
   */
  async addTrailBundle(bundleUrl, spritesheetUrl, aliasPrefix) {
    if (!this._trailReady || !this._trailFx) return false;
    try {
      const ssAlias = `${aliasPrefix}_spritesheet`;
      PIXI.Assets.add({ alias: ssAlias, src: spritesheetUrl });
      await PIXI.Assets.load(ssAlias);
      const response = await fetch(bundleUrl);
      const bundleSettings = await response.json();

      this._bundleCount++;
      const ID_OFFSET = this._bundleCount * 10000;
      for (const emitter of bundleSettings.emitters || []) {
        const uniqueId = emitter.id + ID_OFFSET;
        this._trailFx.addParticleEmitter(uniqueId, { ...emitter, id: uniqueId });
      }
      const spritesheet = PIXI.Assets.get(ssAlias);
      for (const texName of Object.keys(spritesheet?.textures || {})) {
        try {
          this._trailFx.initSprite(texName, { texture: texName, anchorX: 0.5, anchorY: 0.5 });
        } catch {
          // Already registered — skip
        }
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  async loadAdditionalBundle(bundleUrl, spritesheetUrl, aliasPrefix = 'rfx_extra') {
    if (!this._initialized || !this._fx) {
      return false;
    }

    try {
      // Load spritesheet with unique alias (unpacks frame textures into PIXI cache)
      const ssAlias = `${aliasPrefix}_spritesheet`;
      PIXI.Assets.add({ alias: ssAlias, src: spritesheetUrl });
      await PIXI.Assets.load(ssAlias);

      // Load bundle settings JSON with unique alias
      const bundleAlias = `${aliasPrefix}_bundle`;
      PIXI.Assets.add({ alias: bundleAlias, src: bundleUrl });
      const bundleSettings = await PIXI.Assets.load(bundleAlias);

      // Register emitters with offset IDs to avoid collision with existing emitters.
      // Each bundle gets a unique offset (10000, 20000, 30000...) so IDs never collide.
      // Name-based lookup (getParticleEmitter("Rain")) still works because
      // _nameMaps stores name → settings reference, and settings.id matches the key.
      this._bundleCount++;
      const ID_OFFSET = this._bundleCount * 10000;
      if (bundleSettings.emitters) {
        for (const emitter of bundleSettings.emitters) {
          const uniqueId = emitter.id + ID_OFFSET;
          const settings = { ...emitter, id: uniqueId };
          this._fx.addParticleEmitter(uniqueId, settings);
        }
      }
      if (bundleSettings.sequences) {
        for (const seq of bundleSettings.sequences) {
          const uniqueId = seq.id + ID_OFFSET;
          this._fx.addEffectSequence(uniqueId, { ...seq, id: uniqueId });
        }
      }

      // Register sprite textures from the new spritesheet.
      // initSprite throws on duplicates, so try/catch to skip existing ones.
      const spritesheet = PIXI.Assets.get(ssAlias);
      if (spritesheet?.textures) {
        for (const texName of Object.keys(spritesheet.textures)) {
          try {
            this._fx.initSprite(texName, { texture: texName, anchorX: 0.5, anchorY: 0.5 });
          } catch {
            // Already registered from a previous bundle — skip
          }
        }
      }

      // Register movie clips from the spritesheet.
      // RevoltFX movie clips (componentType 1) use frames prefixed "mc_<componentId>_".
      // We scan the bundle's emitters to find movie clip componentIds, then collect
      // matching frames from the spritesheet and register via initMovieClip.
      if (spritesheet?.textures && bundleSettings.emitters) {
        for (const emitter of bundleSettings.emitters) {
          const ps = emitter.particleSettings;
          if (ps?.componentType === 1 && ps.componentId) {
            const mcId = ps.componentId;
            // Collect frames matching "mc_<componentId>_*.png" pattern, sorted numerically
            const prefix = `mc_${mcId}`;
            const frames = Object.keys(spritesheet.textures)
              .filter(name => name.startsWith(prefix))
              .sort((a, b) => {
                const numA = parseInt(a.match(/(\d+)\.png$/)?.[1] || '0');
                const numB = parseInt(b.match(/(\d+)\.png$/)?.[1] || '0');
                return numA - numB;
              });

            if (frames.length > 0) {
              try {
                this._fx.initMovieClip(mcId, { textures: frames, anchorX: 0.5, anchorY: 0.5 });
              } catch {
                // Already registered — skip
              }
            }
          }
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  // ============================================
  // LIGHTNING EFFECTS
  // ============================================

  /**
   * Emit a lightning bolt effect.
   *
   * The emitter is placed inside a wrapper container that handles rotation.
   * The bolt's native sprite is vertical — we rotate the wrapper so the bolt
   * aligns from start→end. All particle behavior (strand count, duration,
   * scale taper) is defined in lightning.json ("Lightning" emitter).
   *
   * @param {PIXI.Container} parentContainer - Container to add the bolt wrapper into
   * @param {{x: number, y: number}} startPos - Start position (zeus coin) in parent's local space
   * @param {{x: number, y: number}} endPos - End position (pot orb) in parent's local space
   * @returns {{wrapper: PIXI.Container, emitter: object}|null}
   */
  emitLightning(parentContainer, startPos, endPos, fxOverrides = null) {
    if (!this._initialized || !this._fx) {
      return null;
    }
    try {
      // Concurrent bolts (15+) must never share or steal emitters from each other:
      // - autoRecycleOnComplete=false → emitter stays out of the pool until we release it
      // - cloneSettings=true → each bolt gets its own settings tree (no shared mutation)
      // - ownerId → teardown only kills THIS bolt's emitter, never another in-flight one
      const emitter = this._fx.getParticleEmitter("Lightning", false, true);
      if (!emitter) {
        return null;
      }

      // ── Retune THIS bolt, and only this bolt ──
      //
      // Safe because `cloneSettings` above already gave the emitter its own
      // settings tree — the bundle's shared object is never touched, so Zeus's
      // bolt keeps the values the artist authored while another game asks for
      // its own. Same rule the trail overrides follow.
      //
      // Applied BEFORE init: RevoltFX reads spawn count and frequency live off
      // `settings` on every burst, and the component params at each particle's
      // birth, but the first burst can happen inside init.
      //
      //   emitter    spawnCountMin/Max, spawnFrequencyMin/Max — how DENSE
      //   particle   durationMin/Max, scaleX ramp, alpha — how each strand looks
      //   component  animationSpeedMin/Max, loop — how fast the MovieClip
      //              crackles, which is where the wiggle comes from
      if (fxOverrides) {
        const settings = emitter.settings;
        if (settings) {
          if (fxOverrides.emitter) Object.assign(settings, fxOverrides.emitter);
          const ps = settings.particleSettings;
          if (ps) {
            if (fxOverrides.particle) Object.assign(ps, fxOverrides.particle);
            if (fxOverrides.component && ps.componentParams) {
              Object.assign(ps.componentParams, fxOverrides.component);
            }
          }
        }
      }

      // Calculate direction
      const dx = endPos.x - startPos.x;
      const dy = endPos.y - startPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 1) {
        try {
          emitter.autoRecycleOnComplete = true;
          emitter.stop(false);
        } catch { /* ignore */ }
        return null;
      }

      // Rotate so the bolt's vertical axis points from start toward end.
      const rotation = Math.atan2(dx, -dy);

      const ownerId = ++this._fxOwnerSeq;
      const wrapper = new PIXI.Container();
      wrapper.name = "lightning_bolt";
      wrapper.position.set(startPos.x, startPos.y);
      wrapper.rotation = rotation;
      wrapper.scale.set(1.0);
      parentContainer.addChild(wrapper);

      emitter.__fxOwnerId = ownerId;
      wrapper.__fxOwnerId = ownerId;
      emitter.init(wrapper, true, 1);

      return { wrapper, emitter, distance, ownerId };
    } catch {
      return null;
    }
  }

  // ============================================
  // TRAIL EFFECTS (particle_trail bundle)
  // ============================================

  /**
   * Emit a particle trail effect that follows a moving container.
   * Uses the "trail_coin" emitter from the particle_trail bundle.
   *
   * The emitter is attached to `parentContainer` at the position of `targetContainer`.
   * As `targetContainer` moves, call `updateTrailPosition(emitter, x, y)` each frame,
   * or simply place the emitter wrapper at the target's position.
   *
   * @param {PIXI.Container} parentContainer - Container to add the trail into
   * @param {{x: number, y: number}} pos - Initial position in parent's local space
   * @param {object} [opts] - Configuration overrides
   * @param {number} [opts.scale=1.0] - Trail scale
   * @returns {{wrapper: PIXI.Container, emitter: object}|null}
   */
  /**
   * Initialize a dedicated FX instance for trail particle effects.
   *
   * RevoltFX's loadBundleFiles uses hardcoded PIXI aliases (rfx_spritesheet,
   * rfx_bundleSettings) which collide with the main _fx instance in the global
   * PIXI.Assets resolver. To avoid this, we manually load assets with unique
   * aliases and call initBundle() directly on the new FX instance.
   *
   * @param {string} bundleUrl - Path to particle_trail.json
   * @param {string} spritesheetUrl - Path to particle_trail_atlas.json
   * @returns {Promise<boolean>}
   */
  async initTrailBundle(bundleUrl, spritesheetUrl) {
    if (this._trailReady) return true;

    try {
      // Can't use loadBundleFiles — it uses hardcoded "rfx_" PIXI resolver keys
      // that collide with the main FX instance's keys. Instead:
      // 1. Load spritesheet into PIXI cache (unique alias)
      // 2. Fetch bundle JSON directly
      // 3. Call initBundle which scans PIXI.Cache for textures

      PIXI.Assets.add({ alias: 'particle_trail_spritesheet', src: spritesheetUrl });
      await PIXI.Assets.load('particle_trail_spritesheet');

      const response = await fetch(bundleUrl);
      const bundleSettings = await response.json();

      // Any cached clone belongs to the FX instance that made it.
      this._trailSettingsCache?.clear();
      this._trailFx = new FX();
      this._trailFx.initBundle(bundleSettings);
      // ── The cap the bundle ships with is a VOLLEY LIMITER ──
      //
      // `initBundle` assigns `fx.maxParticles = bundleSettings.maxParticles`,
      // and trail.json ships 2000. ParticleEmitter.spawn() then returns early
      // the moment `fx.particleCount` reaches it — so the limit is not per
      // trail, it is shared by every trail in the air at once.
      //
      // One bolt holds `spawnCount / frequency * life` particles:
      // 12 / 0.008 * 0.12 = 180 (see LevelChargingAnimator). 2000 is therefore
      // about ELEVEN bolts, and past that every bolt in the volley starves
      // together — a Golden Mystery multiplier fires one per coin on the board,
      // so the big volleys were drawn thin while a single trail was drawn full.
      //
      // Vladimir, 2026-09-23: *"i want every trail to look as if we had a
      // single one"*. So the cap is raised out of the way rather than tuned:
      // 20000 is ~111 bolts at full density, more than a volley can put up.
      //
      // ── This is the one number to move, either way ──
      //
      // It is also the only lever on how much a huge volley costs. If a real
      // round ever stutters, the "golden trail volley" diag line reports the
      // bolt count and the live-particle estimate — bring that here rather than
      // lowering TRAIL_SPAWN_COUNT, which would thin the single trail too.
      //
      // The per-EMITTER cap in the bundle (1000) is untouched and never binds:
      // each bolt is its own emitter and holds 180.
      this._trailFx.maxParticles = TRAIL_FX_MAX_PARTICLES;

      this._trailTickerFn = () => {
        if (this._trailFx) {
          try { this._trailFx.update(); } catch { /* particle referencing destroyed container */ }
        }
      };
      PIXI.Ticker.shared.add(this._trailTickerFn);

      this._trailReady = true;
      return true;
    } catch (error) {
      this._trailFx = null;
      return false;
    }
  }

  /**
   * Emit a trail particle effect from the particle_trail bundle.
   * Matches original TrailFXManager.emitTrail() exactly:
   *   - emitter.init(parentContainer) — particles render into parent
   *   - emitter.target = target — RevoltFX auto-follows the target each frame
   *   - No wrapper container needed
   *
   * @param {PIXI.Container} parentContainer - Container to render trail particles into
   * @param {PIXI.Container|null} target - Moving container to follow (emitter tracks target.x/y)
   * @param {object} [opts] - Configuration
   * @param {number} [opts.scale=1] - Scale modifier for particle sizes
   * @param {number} [opts.particleLife] - Particle lifetime override, SECONDS.
   *   Controls streak length (length = travel speed × life). Forces a settings
   *   clone so the bundle's own value is left alone.
   * @param {object} [opts.particleOverrides] - Fields written onto
   *   `settings.particleSettings` — the per-particle look: scale ramp, tint,
   *   alpha. RevoltFX reads these at SPAWN, from the settings object itself, so
   *   an override applies to every particle this emitter goes on to make.
   * @param {object} [opts.emitterOverrides] - Scalar fields written onto
   *   `settings` — spawn count and frequency, i.e. how DENSE the trail is.
   *   Read live on each spawn cycle. Flat values only; do not pass `core` or
   *   `particleSettings` through here.
   * @param {number} [opts.spawnRadius] - `settings.core.params.radius`, the
   *   lateral spread of the spawn circle. The emitter core holds this params
   *   object by reference, so writing it is honoured immediately. NOTE it is
   *   multiplied by `scale` at spawn time, exactly as particle scale is.
   * @param {string} [opts.colorKey] - Color tint key ("cc","cd","cm")
   * @returns {object|null} emitter instance (pass to stopTrail)
   *
   * Any override above forces a settings CLONE. The settings object is shared
   * between every emitter of that name, so writing to the original would retune
   * the bundle for the rest of the session.
   */
  emitTrail(parentContainer, target = null, opts = {}) {
    if (!this._trailReady || !this._trailFx) return null;

    // No implicit default that names one bundle's emitter. Callers say which
    // one they want — see LevelChargingAnimator — and a caller that does not
    // is a bug worth hearing about rather than a silent miss.
    const emitterName = opts.emitterName;
    if (!emitterName) {
      console.warn("[FXManager] emitTrail called with no emitterName");
      return null;
    }
    const scaleMod = opts.scale ?? 1;

    try {
      // cloneSettings is normally false — see emitLightning (avoids a per-trail
      // config deepClone). A particleLife override forces it true, because the
      // settings object is SHARED between every emitter of that name and
      // writing to it otherwise would retune the bundle for the whole session.
      const needsOwnSettings =
        opts.particleLife != null ||
        opts.particleOverrides != null ||
        opts.emitterOverrides != null ||
        opts.spawnRadius != null;

      // ── ONE deepClone per LOOK, not one per trail ──
      //
      // `getParticleEmitter(name, auto, true)` runs `deepClone(settings)` on the
      // whole emitter config — see revolt-fx/lib/FX.js:340. Every trail asks for
      // overrides (life, spawn radius, particle ramp, tints), so every trail was
      // deep-cloning that config.
      //
      // Four pips is four clones and nobody notices. A Golden Mystery multiplier
      // fires at EVERY coin on the board, so a full reel is a dozen or more
      // clones in a single frame, and that is the volley that drops frames —
      // reported 2026-09-10, alongside 952ms and 1030ms stalled frames in the
      // traces. The straight bolts and the curved ones are the same call.
      //
      // The overrides in a volley are IDENTICAL — same size, same life, same
      // tint, because they are all the same effect. So the clone is cached by
      // what was written on it, and every trail after the first reuses it
      // through `createParticleEmitterFrom`, which takes a settings object and
      // does NOT clone (FX.js:361). Emitters sharing one settings object is the
      // library's normal mode: with cloneSettings false they all share the
      // bundle's. This one is simply ours, with the overrides already baked in.
      //
      // Never mutated after it is cached — a different look produces a different
      // key and its own clone — so nothing can retune another effect mid-flight.
      let emitter = null;
      if (!needsOwnSettings) {
        emitter = this._trailFx.getParticleEmitter(emitterName, true, false);
      } else {
        const key = `${emitterName}|${opts.particleLife ?? ""}|${opts.spawnRadius ?? ""}|${
          opts.particleOverrides ? JSON.stringify(opts.particleOverrides) : ""
        }|${opts.emitterOverrides ? JSON.stringify(opts.emitterOverrides) : ""}`;

        if (!this._trailSettingsCache) this._trailSettingsCache = new Map();
        const cached = this._trailSettingsCache.get(key);

        if (cached) {
          emitter = this._trailFx.createParticleEmitterFrom(cached, true);
        } else {
          emitter = this._trailFx.getParticleEmitter(emitterName, true, true);

          // How long each particle lives, in seconds — the ONLY lever on how
          // long the streak is. A trail's visible length is speed × particle
          // life, so a caller that wants a tight comet instead of a beam
          // spanning the whole path asks for a shorter life here. Set before
          // init: particles read these at spawn.
          if (opts.particleLife != null && emitter?.settings?.particleSettings) {
            emitter.settings.particleSettings.durationMin = opts.particleLife;
            emitter.settings.particleSettings.durationMax = opts.particleLife;
          }

          // The rest of the look, same rules: written on the clone, before init.
          if (opts.particleOverrides && emitter?.settings?.particleSettings) {
            Object.assign(
              emitter.settings.particleSettings,
              opts.particleOverrides,
            );
          }
          if (opts.emitterOverrides && emitter?.settings) {
            Object.assign(emitter.settings, opts.emitterOverrides);
          }
          if (opts.spawnRadius != null && emitter?.settings?.core?.params) {
            emitter.settings.core.params.radius = opts.spawnRadius;
          }

          if (emitter?.settings) this._trailSettingsCache.set(key, emitter.settings);
        }
      }
      if (!emitter) return null;

      // Init emitter directly into parentContainer (no wrapper — matches original TrailFXManager)
      emitter.init(parentContainer, true, scaleMod);

      // Auto-follow target — RevoltFX tracks target.x/y each frame
      if (target) {
        emitter.target = target;
      }

      return emitter;
    } catch (error) {
      return null;
    }
  }

  /**
   * Emit a lightning ball (energy cluster) effect.
   *
   * Uses the "LightningBall" emitter from the JSON — a dense cluster of
   * short, squished, randomly rotated particles forming a glowing ball.
   * All particle behavior is defined in lightning.json.
   *
   * @param {PIXI.Container} parentContainer - Container to add the ball wrapper into
   * @param {{x: number, y: number}} pos - Ball center position in parent's local space
   * @param {object} [opts] - Configuration overrides
   * @param {number} [opts.scale=0.4] - Base scale of the ball
   * @returns {{wrapper: PIXI.Container, emitter: object}|null}
   */
  emitBall(parentContainer, pos, opts = {}) {
    if (!this._initialized || !this._fx) return null;
    try {
      // cloneSettings=false — see emitLightning (avoids per-ball config deepClone)
      const emitter = this._fx.getParticleEmitter("LightningBall", true, false);
      if (!emitter) {
        return null;
      }

      const ballScale = opts.scale ?? 0.4;

      const wrapper = new PIXI.Container();
      wrapper.name = "lightning_ball";
      wrapper.position.set(pos.x, pos.y);
      wrapper.scale.set(ballScale);
      parentContainer.addChild(wrapper);

      emitter.init(wrapper, true, 1);

      return { wrapper, emitter };
    } catch {
      return null;
    }
  }

  /**
   * Stop a trail emitter. Particles fade out naturally.
   * @param {object} emitter - The RevoltFX emitter instance
   * @param {boolean} waitForParticles - If true, existing particles finish their lifecycle
   */
  stopTrail(emitter, waitForParticles = true) {
    if (!emitter) return;

    try {
      // Play particles_end sound when trail stops
      // soundManager.play(SOUND_NAME.particles_end); // TODO: control manually per-feature
      emitter.stop(waitForParticles);
    } catch {
      // Emitter may already be recycled
    }
  }

  /**
   * Immediately stop and recycle an emitter (no fade-out).
   * @param {object} emitter - The RevoltFX emitter instance
   */
  killTrail(emitter) {
    if (!emitter) return;

    try {
      emitter.stop(false);
    } catch {
      // Emitter may already be recycled
    }
  }

  /**
   * Safe teardown for a bolt/ball wrapper + its emitter.
   * Never stops another in-flight effect — ownerId + container must both match.
   * Never destroy({ children: true }) — RevoltFX MovieClips are pooled (see warmUpPools).
   * @param {{ wrapper?: PIXI.Container, emitter?: object, ownerId?: number }|null} fxData
   */
  releaseOwnedFx(fxData) {
    if (!fxData) return;
    const { emitter, wrapper, ownerId } = fxData;

    const stillOurs =
      emitter &&
      !emitter.__recycled &&
      emitter.container === wrapper &&
      (ownerId == null || emitter.__fxOwnerId === ownerId);

    if (stillOurs) {
      try {
        emitter.autoRecycleOnComplete = true;
        emitter.stop(false);
      } catch {
        // Already recycled mid-stop
      }
    }

    if (wrapper && !wrapper.destroyed) {
      if (wrapper.parent) wrapper.parent.removeChild(wrapper);
      wrapper.destroy({ children: false });
    }
  }

  // ============================================
  // RAIN EFFECT
  // ============================================

  /**
   * Start the rain particle effect inside the given container.
   * @param {PIXI.Container} container
   * @returns {{wrapper: PIXI.Container, emitter: object}|null}
   */
  startRain(container) {
    if (!this._initialized || !this._fx) return null;
    if (this._activeRain) return null; // already running

    try {
      const emitter = this._fx.getParticleEmitter("Rain", true, false);

      const wrapper = new PIXI.Container();
      wrapper.name = "rain_fx";
      container.addChild(wrapper);
      emitter.init(wrapper, true, 1);

      this._activeRain = { wrapper, emitter };
      return this._activeRain;
    } catch (error) {
      return null;
    }
  }

  /** Stop the active rain emitter and remove from display. */
  stopRain() {
    if (this._activeRain) {
      try { this._activeRain.emitter.stop(false); } catch { /* recycled */ }
      const w = this._activeRain.wrapper;
      if (w?.parent) w.parent.removeChild(w);
      if (w && !w.destroyed) w.destroy({ children: true });
      this._activeRain = null;
    }
  }

  get isRainPlaying() { return this._activeRain !== null; }

  // ============================================
  // COIN SHOWER EFFECT
  // ============================================

  /**
   * Start the coin shower particle effect inside the given container.
   * Used by GJP and Total Win popups — coins fall from top of screen.
   * @param {PIXI.Container} container
   * @returns {{wrapper: PIXI.Container, emitter: object}|null}
   */
  startCoinShower(container) {
    if (!this._initialized || !this._fx) return null;
    if (this._activeCoinShower) return null; // already running

    try {
      const emitter = this._fx.getParticleEmitter("Falling_Coins", true, false);

      const wrapper = new PIXI.Container();
      wrapper.name = "coin_shower_fx";
      container.addChild(wrapper);
      emitter.init(wrapper, true, 1);

      this._activeCoinShower = { wrapper, emitter };
      return this._activeCoinShower;
    } catch (error) {
      return null;
    }
  }

  /** Stop the active coin shower emitter and remove from display. */
  stopCoinShower() {
    if (this._activeCoinShower) {
      try { this._activeCoinShower.emitter.stop(false); } catch { /* recycled */ }
      const w = this._activeCoinShower.wrapper;
      if (w?.parent) w.parent.removeChild(w);
      if (w && !w.destroyed) w.destroy({ children: true });
      this._activeCoinShower = null;
    }
  }

  get isCoinShowerPlaying() { return this._activeCoinShower !== null; }

  // ============================================
  // LIFECYCLE
  // ============================================

  /**
   * Warm the RevoltFX object pools off-screen so the FIRST bolt/trail of a bonus
   * doesn't pay the cold-start (empty pools → new ParticleEmitter, new cores, new
   * Particles, and MovieClip frame-array builds — a visible hitch on slow devices
   * like iPhone 11). Call once, during asset load while the loading screen is up.
   *
   * Emits the bonus-relevant emitters into a DETACHED container (never parented to
   * a stage, so nothing renders — no flash), lets a couple of real frames pass so
   * fx.update() spawns particles into the pools, then recycles everything back.
   * Best-effort: any failure is swallowed — the game just pays the cold-start as before.
   *
   * @returns {Promise<void>}
   */
  async warmUpPools() {
    if (this._poolsWarmed) return;
    this._poolsWarmed = true;

    // Resolve on the next frame, but fall back to a timeout so a throttled
    // requestAnimationFrame (e.g. backgrounded tab during load) can never hang
    // the load sequence that awaits this warmup.
    const nextFrame = () =>
      new Promise((resolve) => {
        let done = false;
        const finish = () => { if (!done) { done = true; resolve(); } };
        requestAnimationFrame(finish);
        setTimeout(finish, 100);
      });

    const warm = async (fx, names) => {
      if (!fx) return;

      // Detached container — never added to any stage, so particles spawned here
      // render nowhere. No visual, no flash.
      const container = new PIXI.Container();
      const emitters = [];
      for (const name of names) {
        try {
          const emitter = fx.getParticleEmitter(name, true, false);
          if (emitter) {
            emitter.init(container, true, 1);
            emitters.push(emitter);
          }
        } catch {
          // Emitter name not present in this bundle — skip
        }
      }

      if (emitters.length > 0) {
        // Let real time pass so fx.update() spawns particles into the pools.
        // Drive update() manually too, so warmup works even if the shared ticker
        // isn't running yet during the load phase.
        for (let i = 0; i < 2; i++) {
          await nextFrame();
          try { fx.update(); } catch { /* ignore transient warmup errors */ }
        }
        // Recycle emitters + their particles back to the pools (no fade-out).
        for (const emitter of emitters) {
          try { emitter.stop(false); } catch { /* already recycled */ }
        }
      }

      // Detach any lingering display objects WITHOUT destroying them (they may be
      // pooled), then destroy just the now-empty container.
      if (!container.destroyed) {
        container.removeChildren();
        container.destroy({ children: false });
      }
    };

    try {
      // Bonus hot-path emitters: bolts (Lightning + ball) and value trails.
      await warm(this._fx, ["Lightning", "LightningBall"]);
      // The bundle's OWN emitters. `trail_coin` belonged to the old
      // zeus_particles bundle and does not exist in trail.json, so this was
      // warming a name that is not there — which warms nothing, and leaves the
      // first real trail of the round to pay the cost this exists to avoid.
      // Both are listed because either feature may ship alone.
      await warm(this._trailFx, ["trail_blue", "trail_gold", "trail_coin"]);
    } catch {
      // Warmup is best-effort — never block or break load on failure.
    }
  }

  /**
   * Full cleanup — call on application shutdown.
   */
  destroy() {
    this.stopRain();
    this.stopCoinShower();

    if (this._tickerFn) {
      PIXI.Ticker.shared.remove(this._tickerFn);
      this._tickerFn = null;
    }

    if (this._fx) {
      try {
        this._fx.dispose();
      } catch {
        // ignore
      }
      this._fx = null;
    }

    // Clean up trail FX instance
    if (this._trailTickerFn) {
      PIXI.Ticker.shared.remove(this._trailTickerFn);
      this._trailTickerFn = null;
    }
    if (this._trailFx) {
      try {
        this._trailFx.dispose();
      } catch {
        // ignore
      }
      this._trailFx = null;
    }
    this._trailSettingsCache?.clear();
    this._trailReady = false;

    this._initialized = false;
    this._initializing = false;
  }

  /**
   * Check if the manager is ready to emit.
   */
  get isReady() {
    return this._initialized && this._fx !== null;
  }

  /**
   * Check if the trail particle FX is ready to emit.
   */
  get isTrailReady() {
    return this._trailReady && this._trailFx !== null;
  }

  /**
   * Native pixel height of one lightning sprite frame at scale=1.
   * Read directly from the PIXI texture cache after the bundle loads —
   * same pattern as LogoLandscape reads its texture dimensions.
   * Falls back to 459 (the confirmed atlas value) if not yet loaded.
   */
  get lightningNativeHeight() {
    const tex = PIXI.Assets.get("mc_lightning_Animation_0.png");
    return tex?.height ?? 459;
  }

  /**
   * The bolt art's native WIDTH — 74 against its 459 height.
   *
   * Needed by any caller that sizes the bolt's thickness from its LENGTH rather
   * than from a cell, which is the only way to keep the shape when the gap is
   * short. See fireLightningBolt's widthFraction.
   */
  get lightningNativeWidth() {
    const tex = PIXI.Assets.get("mc_lightning_Animation_0.png");
    return tex?.width ?? 74;
  }

  /**
   * Get available emitter names (for debugging).
   */
  _getEmitterNames() {
    if (!this._fx?._nameMaps?.emitters) return [];
    return Object.keys(this._fx._nameMaps.emitters);
  }
}

// Singleton export
const FXManager = new FXManagerClass();
export default FXManager;
